import { getNormalizedIrradiance } from '../data/irradianceZones';

const ELECTRICITY_RATE = 0.2467; // £ per kWh
const EXPORT_RATE = 0.12;        // £ per kWh
const INSTALL_COST_PER_KWP = 1400; // £ per kWp
const LIFETIME_YEARS = 25;
const ANNUAL_INFLATION_RATE = 0.02;

const ORIENTATION_FACTORS = {
  south: 1.0,
  southeast: 0.93,
  southwest: 0.93,
  east: 0.79,
  west: 0.79,
  north: 0.64,
  northeast: 0.64,
  northwest: 0.64,
};

function normalizeOrientation(orientation) {
  const map = {
    'N': 'north',
    'NE': 'northeast',
    'E': 'east',
    'SE': 'southeast',
    'S': 'south',
    'SW': 'southwest',
    'W': 'west',
    'NW': 'northwest'
  };
  return map[orientation.toUpperCase()] || 'south'; // fallback to 'south'
}

/**
 * Calculates base solar yield per kWp using MCS method (kWh per year per kWp)
 * @param {number} latitude - Latitude of property (not used in this simplified model)
 * @returns {number} Base yield (default 950)
 */
function getBaseYield() {
  return 950; // kWh/kWp/year, UK average per MCS
}

/**
 * Calculates shading adjustment factor
 * @param {number} shadingPercent - e.g. 50 for 50%
 * @param {string} shadingTime - 'morning', 'afternoon', or 'all-day'
 * @returns {number} shading factor (0 to 1)
 */
function getShadingFactor(shadingPercent, shadingTime) {
  const shading = Math.min(Math.max(shadingPercent, 0), 100) / 100; // Clamp between 0–100

  // Weighting how much shading at each time affects total generation
  const timeWeights = {
    morning: 0.3,      // Morning shading impacts 30% of daily output
    afternoon: 0.7,    // Afternoon shading impacts 70%
    'all-day': 1.0     // Total shading
  };

  const weight = timeWeights[shadingTime?.toLowerCase()] ?? 0;

  // Effective unshaded fraction = 1 - shading * impactWeight
  return 1 - (shading * weight);
  
}


/**
 * Calculates annual solar generation in kWh
 * @param {number} systemSizeKwp - System size in kWp
 * @param {string} orientation - e.g. 'south', 'east' etc.
 * @param {number} shadingPercent - % shading (0-100)
 * @param {string} shadingTime - 'morning', 'afternoon', 'all-day'
 * @returns {number} Annual solar generation (kWh)
 */
function calculateAnnualGeneration(systemSizeKwp, orientation, shadingPercent, shadingTime) {
  orientation = normalizeOrientation(orientation);  // <-- UPDATED LINE

  const baseYield = getBaseYield();
  const orientationFactor = ORIENTATION_FACTORS[orientation.toLowerCase()] || 1.0;
  const shadingFactor = getShadingFactor(shadingPercent, shadingTime);

    return systemSizeKwp * baseYield * orientationFactor * shadingFactor;
}

/**
 * Calculates self-consumption and export kWh
 * @param {number} annualGeneration - Annual solar generation (kWh)
 * @param {number} annualConsumption - Annual electricity consumption (kWh)
 * @param {number} occupancyFactor - % of electricity consumed at home (0-1), e.g. 0.75 for 75%
 * @returns {object} { selfConsumed, exported }
 */
function calculateConsumptionExport(annualGeneration, annualConsumption, occupancyFactor) {
  // Estimate self-consumption as min of generation and consumption adjusted by occupancy
  const daytimeConsumption = annualConsumption * occupancyFactor;
  const possibleSelfConsumed = Math.min(daytimeConsumption, annualGeneration * occupancyFactor);
  
  // Export is generation minus self-consumed (cannot be negative)
  const exported = Math.max(annualGeneration - possibleSelfConsumed, 0);

  return {
    selfConsumed: possibleSelfConsumed,
    exported,
  };
}

/**
 * Calculates year 1 savings (£)
 * @param {number} selfConsumedKwh
 * @param {number} exportedKwh
 * @returns {number} year 1 savings in £
 */
function calculateYear1Savings(selfConsumedKwh, exportedKwh) {
  return (selfConsumedKwh * ELECTRICITY_RATE) + (exportedKwh * EXPORT_RATE);
}

/**
 * Calculates lifetime savings over 25 years with 2% annual inflation on electricity/export rates
 * @param {number} year1SelfConsumedKwh
 * @param {number} year1ExportedKwh
 * @returns {number} lifetime savings in £
 */
function calculateLifetimeSavings(year1SelfConsumedKwh, year1ExportedKwh) {
  let totalSavings = 0;
  let electricityRate = ELECTRICITY_RATE;
  let exportRate = EXPORT_RATE;

  for (let year = 1; year <= LIFETIME_YEARS; year++) {
    const yearSavings = (year1SelfConsumedKwh * electricityRate) + (year1ExportedKwh * exportRate);
    totalSavings += yearSavings;

    electricityRate *= (1 + ANNUAL_INFLATION_RATE);
    exportRate *= (1 + ANNUAL_INFLATION_RATE);
  }

  return totalSavings;
}

/**
 * Calculates installation cost (£)
 * @param {number} systemSizeKwp
 * @returns {number} installation cost (£)
 */
function calculateInstallationCost(systemSizeKwp) {
  return systemSizeKwp * INSTALL_COST_PER_KWP;
}

/**
 * Calculates payback period (years) to recoup installation cost
 * @param {number} year1Savings - £ savings in year 1
 * @param {number} installationCost - £
 * @param {number} inflationRate - annual savings growth (default 2%)
 * @param {number} maxYears - max years to check (default 25)
 * @returns {number|null} payback period in years or null if no payback within maxYears
 */
function calculatePaybackPeriod({
  year1Savings,
  installationCost,
  inflationRate = ANNUAL_INFLATION_RATE,
  maxYears = LIFETIME_YEARS,
}) {
  let cumulativeSavings = 0;
  let currentYearSavings = year1Savings;

  for (let year = 1; year <= maxYears; year++) {
    cumulativeSavings += currentYearSavings;
    if (cumulativeSavings >= installationCost) {
      return year;
    }
    currentYearSavings *= (1 + inflationRate);
  }

  return null;
}

// ─── Viability Score ──────────────────────────────────────────────────────────

// Piecewise-linear interpolation between the five approved consumption breakpoints.
function getConsumptionScore(kwh) {
  if (kwh <= 1000) return 2;
  if (kwh <= 2000) return 2 + ((kwh - 1000) / 1000) * 3;   // 2 → 5
  if (kwh <= 2900) return 5 + ((kwh - 2000) / 900)  * 2;   // 5 → 7
  if (kwh <= 4000) return 7 + ((kwh - 2900) / 1100) * 2;   // 7 → 9
  if (kwh <= 5000) return 9 + ((kwh - 4000) / 1000) * 1;   // 9 → 10
  return 10;
}

/**
 * Calculates a 0–100 solar viability score from wizard inputs and derived results.
 * @param {object} formData - accumulated wizard inputs
 * @param {object} calculatedResults - { paybackPeriod: number|null }
 * @returns {{ totalScore: number, band: string, breakdown: object }}
 */
function calculateViabilityScore(formData, calculatedResults) {
  const {
    orientation,
    shadingPercent,
    shadingTime,
    postcode,
    occupancySchedule,
    electricityConsumption,
    roofCondition,
    evOwnership,
    currentHeating,
    heatPumpPlans,
  } = formData;

  const { paybackPeriod } = calculatedResults;

  // 1. Orientation — 0–20 pts
  const orientationMap = {
    south: 20, southeast: 17, southwest: 17,
    east: 11,  west: 11,
    northeast: 5, northwest: 5, north: 5,
  };
  const orientationScore = orientationMap[normalizeOrientation(orientation || 'S')] ?? 5;

  // 2. Shading — 0–20 pts (reuses getShadingFactor for consistency with generation calc)
  const shadingScore = Math.round(getShadingFactor(shadingPercent ?? 0, shadingTime ?? 'all-day') * 20);

  // 3. Postcode irradiance — 7–15 pts (MCS MIS 3002 zone Kk, normalised to UK zone range)
  const irradianceScore = Math.round(7 + getNormalizedIrradiance(postcode) * 8);

  // 4. Occupancy — 0–13 pts
  const occupancyMap = {
    'someone home all day':        13,
    'home half the day':            8,
    'nobody home during the day':   3,
  };
  const occupancyScore = occupancyMap[(occupancySchedule || '').toLowerCase()] ?? 5;

  // 5. Electricity consumption — 0–10 pts (piecewise linear, centred on UK avg 2,900 kWh)
  const consumptionScore = Math.round(getConsumptionScore(electricityConsumption ?? 0));

  // 6. Payback period — 0–10 pts
  let paybackScore;
  if      (!paybackPeriod || paybackPeriod > 18) paybackScore = 0;
  else if (paybackPeriod <= 7)                   paybackScore = 10;
  else if (paybackPeriod <= 9)                   paybackScore = 8;
  else if (paybackPeriod <= 11)                  paybackScore = 6;
  else if (paybackPeriod <= 14)                  paybackScore = 4;
  else                                           paybackScore = 2; // 15–18

  // 7. Roof condition — 0–7 pts (v2 field; defaults to 0 until wizard step is built)
  const roofConditionMap = { excellent: 7, good: 5, fair: 3, poor: 1 };
  const roofConditionScore = roofConditionMap[(roofCondition || '').toLowerCase()] ?? 0;

  // 8. EV ownership — 0–5 pts (v2 bonus field; no penalty when absent)
  const evMap = { 'yes: 1 vehicle': 3, 'yes: more than 1 vehicle': 5, 'no': 0 };
  const evScore = evMap[(evOwnership || '').toLowerCase()] ?? 0;

  // 9. Heat pump / electrification — 0–6 pts
  const currentHeatingLower = (currentHeating || '').toLowerCase();
  const heatPumpPlansLower  = (heatPumpPlans  || '').toLowerCase();
  let heatPumpScore = 0;
  if      (currentHeatingLower === 'heat pump')                                                                          heatPumpScore = 6;
  else if (currentHeatingLower === 'electric heating' && heatPumpPlansLower === 'yes: planning to install a heat pump')  heatPumpScore = 5;
  else if (currentHeatingLower === 'electric heating' && heatPumpPlansLower === 'not sure yet')                          heatPumpScore = 4;
  else if (currentHeatingLower === 'electric heating')                                                                   heatPumpScore = 3;
  else if (heatPumpPlansLower === 'yes: planning to install a heat pump')                                                heatPumpScore = 3;
  else if (heatPumpPlansLower === 'not sure yet')                                                                        heatPumpScore = 1;
  else                                                                                                                   heatPumpScore = 0;

  const totalScore = Math.min(100, Math.max(0,
    orientationScore + shadingScore + irradianceScore +
    occupancyScore + consumptionScore + paybackScore +
    roofConditionScore + evScore + heatPumpScore
  ));

  let band;
  if      (totalScore >= 85) band = 'Excellent';
  else if (totalScore >= 70) band = 'Good';
  else if (totalScore >= 55) band = 'Moderate';
  else if (totalScore >= 40) band = 'Below average';
  else                       band = 'Poor';

  return {
    totalScore,
    band,
    breakdown: {
      orientation:   orientationScore,
      shading:       shadingScore,
      irradiance:    irradianceScore,
      occupancy:     occupancyScore,
      consumption:   consumptionScore,
      payback:       paybackScore,
      roofCondition: roofConditionScore,
      evOwnership:   evScore,
      heatPump:      heatPumpScore,
    },
  };
}

export {
  calculateAnnualGeneration,
  calculateConsumptionExport,
  calculateYear1Savings,
  calculateLifetimeSavings,
  calculateInstallationCost,
  calculatePaybackPeriod,
  normalizeOrientation,
  calculateViabilityScore,
};
